import React, { useState, useEffect, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";

// ... (MonthDropdown and YearDropdown remain the same)
const MonthDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

   // Step 2: Handle auto-scroll when opened
  useEffect(() => {
    if (open && scrollRef.current) {
      const index = value; // Value is already the 0-11 index
      const itemHeight = 40; 
      const containerHeight = 160; 
      
      // Calculate scroll to center the month
      scrollRef.current.scrollTop = (index * itemHeight) - (containerHeight / 2) + (itemHeight / 2);
    }
  }, [open, value]);
  return (
    <div className="relative w-[110px]">
      <div className="border rounded px-2 py-1 text-xs cursor-pointer bg-white text-gray-333 flex justify-between items-center text-center" onClick={() => setOpen(!open)}>
        {monthNames[value]} <FiChevronDown className={`ml-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div ref={scrollRef} className="absolute left-0 mt-1 w-full bg-white border rounded shadow-lg z-20 max-h-40 overflow-y-auto">
          {monthNames.map((m, i) => (
            <div key={i} className={`px-3 py-2 cursor-pointer text-xs text-gray-333 flex items-center justify-center ${i === value ? "bg-gray-50 font-bold" : "hover:bg-gray-100"}`} style={{ height: "40px" }} onClick={() => { onChange(i); setOpen(false); }}>{m}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const YearDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);
  const years = Array.from({ length: 120 }, (_, i) => 2000 + i);
  useEffect(() => {
    if (open && scrollRef.current) {
      const targetYear = value || new Date().getFullYear();
      const index = years.indexOf(targetYear);
      if (index !== -1) {
        const itemHeight = 40;
        const containerHeight = 160;
        scrollRef.current.scrollTop = (index * itemHeight) - (containerHeight / 2) + (itemHeight / 2);
      }
    }
  }, [open, value, years]);
  return (
    <div className="relative w-[80px]">
      <div className="border rounded px-2 py-1 text-xs cursor-pointer bg-white text-gray-333 text-center flex justify-between items-center" onClick={() => setOpen(!open)}>
        {value} <FiChevronDown className={`ml-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div ref={scrollRef} className="absolute left-0 mt-1 w-full bg-white border rounded shadow-lg z-20 max-h-40 overflow-y-auto text-center">
          {years.map((yr) => (
            <div key={yr} className={`p-2 cursor-pointer text-xs text-gray-333 flex items-center justify-center ${yr === value ? "bg-gray-50" : "hover:bg-gray-100"}`} style={{ height: "40px" }} onClick={() => { onChange(yr); setOpen(false); }}>{yr}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DateRangePicker = ({ onChange, selectedStart, selectedEnd, emitDefaultOnMount = false }) => {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const [selectionMode, setSelectionMode] = useState("custom");
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [hasEmittedDefault, setHasEmittedDefault] = useState(false);

  useEffect(() => { generateCalendar(); }, [month, year]);

  // FIX: Sync internal state AND selection mode with props when modal opens
  useEffect(() => {
    if (selectedStart) {
      const sParts = selectedStart.split("-");
      const sDate = new Date(sParts[0], sParts[1] - 1, sParts[2]);
      setStartDate(sDate);
      // Update the month/year view to match the selected start date
      setMonth(sDate.getMonth());
      setYear(sDate.getFullYear());

      if (selectedEnd) {
        const eParts = selectedEnd.split("-");
        const eDate = new Date(eParts[0], eParts[1] - 1, eParts[2]);
        setEndDate(eDate);

        // Determine mode based on whether dates are identical
        if (selectedStart === selectedEnd) {
          setSelectionMode("day");
        } else {
          setSelectionMode("custom");
        }
      }
    }
  }, [selectedStart, selectedEnd]);

  useEffect(() => {
    if (emitDefaultOnMount && !hasEmittedDefault && !selectedStart && !selectedEnd) {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(firstDay);
      setEndDate(lastDay);
      onChange({ startDate: formatDateToISO(firstDay), endDate: formatDateToISO(lastDay) });
      setHasEmittedDefault(true);
    }
  }, [emitDefaultOnMount, hasEmittedDefault]);

  const formatDateToISO = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const generateCalendar = () => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    let temp = []; let week = [];
    for (let i = 0; i < firstDay; i++) week.push(null);
    for (let d = 1; d <= totalDays; d++) {
      week.push(new Date(year, month, d));
      if (week.length === 7) { temp.push(week); week = []; }
    }
    if (week.length) temp.push(week);
    setCalendar(temp);
  };

  const handleSelect = (date) => {
    if (date > todayStart) return;

    if (selectionMode === "day") {
      setStartDate(date);
      setEndDate(date);
    } else {
      if ((startDate && date.getTime() === startDate.getTime()) || (endDate && date.getTime() === endDate.getTime())) {
        setStartDate(null); setEndDate(null);
      } else if (!startDate || (startDate && endDate)) {
        setStartDate(date); setEndDate(null);
      } else {
        if (date < startDate) { setStartDate(date); setEndDate(null); }
        else { setEndDate(date); }
      }
    }
  };

  const handleApply = () => {
    onChange({ startDate: formatDateToISO(startDate), endDate: formatDateToISO(endDate) });
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    onChange({ startDate: null, endDate: null });
  };

  const resetSelectionInternally = (mode) => {
    setSelectionMode(mode);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <div className="w-[440px] p-3 bg-white border border-gray-300 rounded-xl shadow-sm select-none">
      <div className="flex items-center justify-between mb-3 gap-4">
        <div className="flex bg-gray-100 p-0.5 rounded-lg w-40">
          <button
            type="button"
            className={`flex-1 py-1 text-[10px] font-bold  rounded-md transition-all ${selectionMode === "day" ? "bg-white shadow-sm text-gray-800" : "text-gray-500"}`}
            onClick={(e) => { e.stopPropagation(); resetSelectionInternally("day"); }}
          >
            Day
          </button>
          <button
            type="button"
            className={`flex-1 py-1 text-[10px] font-bold  rounded-md transition-all ${selectionMode === "custom" ? "bg-white shadow-sm text-gray-800" : "text-gray-500"}`}
            onClick={(e) => { e.stopPropagation(); resetSelectionInternally("custom"); }}
          >
            Custom
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button className="px-2 py-1 rounded text-[11px] text-gray-500 border border-gray-200 hover:bg-gray-50 disabled:opacity-30" onClick={handleClear} disabled={!startDate}>Clear</button>
          <button className="px-3 py-1 rounded text-[11px] bg-gradient text-white disabled:opacity-50" disabled={!startDate || (selectionMode === "custom" && !endDate)} onClick={handleApply}>Apply</button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <button className="px-2 py-1 rounded text-xs text-gray-333 hover:bg-gray-200" onClick={() => (month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1))}>{"<"}</button>
        <div className="flex flex-1 justify-center gap-2">
            <MonthDropdown value={month} onChange={setMonth} />
            <YearDropdown value={year} onChange={setYear} />
        </div>
        <button
          className="px-2 py-1 rounded text-xs text-gray-333 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
          onClick={() => (month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1))}
          disabled={year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth())}
        >
          {">"}
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-gray-400 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>

      {calendar.map((week, i) => (
        <div key={i} className="grid grid-cols-7 text-center">
          {week.map((date, idx) => {
            const isSelected = (startDate && date?.getTime() === startDate?.getTime()) || (endDate && date?.getTime() === endDate?.getTime());
            const inRange = date && startDate && endDate && date >= startDate && date <= endDate;
            const isFutureDate = date && date > todayStart;
            const disabled = isFutureDate || (selectionMode === "custom" && startDate && !endDate && date < startDate);

            return (
              <div
                key={idx}
                className={`m-0.5 py-1 rounded cursor-pointer text-xs transition-colors
                  ${isSelected ? "bg-gradient text-white font-bold" : ""}
                  ${inRange && !isSelected ? "bg-gray-100" : ""}
                  ${disabled ? "text-gray-200 cursor-not-allowed" : date && !isSelected ? "hover:bg-gray-100" : ""}
                `}
                onClick={() => date && !disabled && handleSelect(date)}
              >
                {date ? date.getDate() : ""}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default DateRangePicker;
